import { useCallback, useRef, useState } from "react";
import { useForm, useWatch, type FieldErrors } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { Quote } from "@/shared/types/Quote.interface";
import { RegexUtils } from "@/shared/utils/RegexUtils";
import { UIUtils } from "@/shared/utils/UIUtils";
import { QuoteAPI, type PaperlessRequestBody } from "@/shared/utils/QuoteAPI";
import { QuoteDataUtils } from "@/shared/utils/QuoteDataUtils";
import ErrorUtils from "@/shared/utils/ErrorUtils";
import Strings from "@/shared/utils/Strings.constants";
import { PublicConfig } from "@/shared/PublicConfig";
import { CaCheckoutFields, CaCheckoutFieldsSchema, CaMailingAddressSchema } from "../schema/PtzCaQuote";

// Module-level so the `name` array is referentially stable: react-hook-form's `useWatch` lists `name` in
// its subscription effect deps, and an inline literal made it tear down and resubscribe every render.
const CHECKOUT_WATCH_FIELDS = ["phone", "termsConsent", "paperless", "address"] as const;
import { PtzCaDataUtils } from "../utils/PtzCaDataUtils";

// Component-facing mailing-address shape. `province`/`postal` map to the RHF/stored
// `address.state`/`address.zipCode` (and `mailingAddress.state`/`zipCode` once persisted).
interface MailingAddressView {
    street1: string;
    street2: string;
    city: string;
    province: string;
    postal: string;
}

// Maps a component address field to its RHF (and schema) path segment.
const ADDRESS_FIELD_TO_RHF = { street1: "street1", street2: "street2", city: "city", postal: "zipCode" } as const;
type EditableAddressField = keyof typeof ADDRESS_FIELD_TO_RHF;

// First-error-wins focus order, matching the on-screen field order. Maps each possible error to the DOM
// id of its input so we can scroll/focus it after a failed Continue. RHF's setFocus can't reach these
// inputs (they're controlled via setValue, not registered with refs), so we target the DOM directly —
// the same approach the US Terms / PolicyErrorController use.
const ERROR_FOCUS_ORDER: ReadonlyArray<{ id: string; hasError: (e: FieldErrors<CaCheckoutFields>) => boolean }> = [
    { id: "ca-phone", hasError: e => !!e.phone },
    { id: "ca-terms-consent", hasError: e => !!e.termsConsent },
    { id: "ca-street1", hasError: e => !!e.address?.street1 },
    { id: "ca-city", hasError: e => !!e.address?.city },
    { id: "ca-province", hasError: e => !!e.address?.state },
    { id: "ca-postal", hasError: e => !!e.address?.zipCode }
];

/** Scroll to and focus the first errored field after a failed Continue. */
function scrollToFirstError(formErrors: FieldErrors<CaCheckoutFields>) {
    if (typeof document === "undefined") return;
    const first = ERROR_FOCUS_ORDER.find(field => field.hasError(formErrors));
    if (!first) return;
    // Defer so the error UI (expanded hints / danger borders) has rendered and layout has settled.
    setTimeout(() => {
        const el = document.getElementById(first.id);
        if (!el) return;
        el.focus({ preventScroll: true });
        el.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 0);
}

/** Validate an already-stored billing address (used to seed the initial unlock state on rehydration). */
function validateStoredAddress(a?: { street1?: string; street2?: string; city?: string; state?: string; zipCode?: string }) {
    return CaMailingAddressSchema.safeParse({
        street1: a?.street1 ?? "",
        street2: a?.street2 || undefined,
        city: a?.city ?? "",
        state: a?.state ?? "",
        zipCode: a?.zipCode ?? ""
    });
}

/**
 * Gates the Chase payment iframe behind data we collect outside it.
 *
 * Because we can't validate the iframe's own submit (no programmatic submit, unlike Stripe), the
 * required fields must be satisfied *before* the iframe is interactive, and they must be persisted
 * to the stored quote because finalize binds server-side from the stored quote (not from anything
 * passed at finalize time):
 *   - a valid mobile phone (`quote.phone`)
 *   - terms accepted (`quote.extra.termsConsent`, same field the US writes)
 *   - paperless preference (`quote.paperless`); when opted OUT, a valid mailing address persisted to
 *     `quote.mailingAddress` (documents are mailed there)
 *
 * Field state, validation, and error messages are owned by react-hook-form + zod
 * (`CaCheckoutFieldsSchema`) — the same stack as every other step. The Terms step has an explicit
 * **Continue** button (`submitStep`), so this is the conventional submit flow: validate on click (errors
 * surface, button never blocks on validity), and on success persist the whole set to the stored quote in
 * one mutation and mark the step complete. `isTermsComplete` re-locks if the customer edits afterward
 * (unsaved changes), so they must Continue again to re-persist and advance.
 */
export function useCheckoutValidation(quote: Quote) {
    const { appState } = useAppLayerContext();
    const { updateQuote } = appState;

    const savedPhone = quote?.phone ?? "";
    const savedPhoneValid = RegexUtils.phoneRegex.test(savedPhone);
    const savedTermsConsent = quote?.extra?.termsConsent ?? false;
    // Paperless defaults to opted-in (checked); a mailing address is only required when opted out.
    const savedPaperless = quote?.paperless ?? true;
    const savedAddress = quote?.mailingAddress;

    const form = useForm<CaCheckoutFields>({
        resolver: zodResolver(CaCheckoutFieldsSchema),
        // Validate when the customer clicks Continue, then keep errors live as they fix them.
        mode: "onSubmit",
        reValidateMode: "onChange",
        defaultValues: {
            // Mask the initial values — a phone/postal prefilled from Step 1 or rehydrated on refresh
            // comes back unformatted, and onChange (the only other place we mask) won't have fired yet.
            phone: UIUtils.maskPhone(savedPhone),
            termsConsent: savedTermsConsent,
            paperless: savedPaperless,
            address: {
                street1: savedAddress?.street1 ?? "",
                street2: savedAddress?.street2 ?? "",
                city: savedAddress?.city ?? "",
                state: savedAddress?.state ?? "",
                zipCode: UIUtils.maskPostalCodeCA(savedAddress?.zipCode ?? "")
            }
        }
    });
    const { control, setValue, formState, handleSubmit, reset, setError } = form;
    const { errors, isDirty } = formState;
    // Named subscription (these four fields are the whole schema) so each value keeps its schema type;
    // the no-arg form types the snapshot as DeepPartial. First render yields `defaultValues`, which is
    // also what `watch()` returned before any interaction.
    const [phone, termsConsent, paperless, address] = useWatch({ control, name: CHECKOUT_WATCH_FIELDS });
    const values = { phone, termsConsent, paperless, address };

    const queryClient = useQueryClient();

    // True once the step has been successfully persisted via Continue. Seeded true when a rehydrated
    // quote already satisfies everything (resume straight into Payment).
    const [savedOnce, setSavedOnce] = useState(() => savedPhoneValid && savedTermsConsent && (savedPaperless || validateStoredAddress(savedAddress).success));
    // True when the most recent persist failed — surfaces an error + retry to the user.
    const [saveError, setSaveError] = useState(false);
    // True while a persist is in flight — dims/locks the step inputs during the round-trip.
    const [isSaving, setIsSaving] = useState(false);

    // Payment unlocks once the step has been saved AND has no unsaved edits since. Editing a field after
    // a successful Continue re-locks Payment until the customer Continues again (so the iframe never
    // binds against a stale stored quote).
    const isTermsComplete = savedOnce && !isDirty;

    // Errors are submit-driven (mode "onSubmit"), so display them directly — no touched gating needed.
    const showPhoneError = !!errors.phone;
    const termsError = errors.termsConsent?.message;
    const addressErrors: Partial<Record<keyof MailingAddressView, string>> = {
        street1: errors.address?.street1?.message,
        street2: errors.address?.street2?.message,
        city: errors.address?.city?.message,
        province: errors.address?.state?.message,
        postal: errors.address?.zipCode?.message
    };

    // After the first Continue, re-validate on each edit so errors clear as the customer fixes them.
    // Before it, stay quiet (don't flash red while they're still filling the form out).
    const submitAttemptedRef = useRef(false);
    const validateOnChange = useCallback(() => ({ shouldDirty: true, shouldValidate: submitAttemptedRef.current }), []);

    const handlePhoneChange = useCallback(
        (value: string) => {
            setValue("phone", UIUtils.maskPhone(value), validateOnChange());
        },
        [setValue, validateOnChange]
    );

    const handleTermsChange = useCallback(
        (value: boolean) => {
            setValue("termsConsent", value, validateOnChange());
        },
        [setValue, validateOnChange]
    );

    const handlePaperlessChange = useCallback(
        (value: boolean) => {
            setValue("paperless", value, validateOnChange());
        },
        [setValue, validateOnChange]
    );

    const handleAddressChange = useCallback(
        (field: keyof MailingAddressView, value: string) => {
            if (field === "province") {
                setValue("address.state", value, validateOnChange());
            } else {
                const rhfKey = ADDRESS_FIELD_TO_RHF[field as EditableAddressField];
                const next = rhfKey === "zipCode" ? UIUtils.maskPostalCodeCA(value) : value;
                setValue(`address.${rhfKey}`, next, validateOnChange());
            }
        },
        [setValue, validateOnChange]
    );

    const handleProvinceChange = useCallback(
        (value: string) => {
            setValue("address.state", value, validateOnChange());
        },
        [setValue, validateOnChange]
    );

    // Persist the (already-validated) checkout fields to the stored quote in one mutation. Patches only
    // the fields that actually changed vs the stored quote. Returns whether the step is safely persisted.
    const persist = useCallback(
        async (v: CaCheckoutFields): Promise<boolean> => {
            const addressParse = CaMailingAddressSchema.safeParse({
                street1: v.address.street1,
                street2: v.address.street2 === "" ? undefined : v.address.street2,
                city: v.address.city,
                state: v.address.state,
                zipCode: v.address.zipCode
            });
            // Compare digits only: our input value is masked while the stored quote holds the raw value.
            const needsPhone = v.phone.replace(/\D/g, "") !== (quote?.phone ?? "").replace(/\D/g, "");
            const needsTerms = !quote?.extra?.termsConsent;
            const needsPaperless = (quote?.paperless ?? undefined) !== v.paperless;
            const storedAddress = quote?.mailingAddress;
            // Only write the address when NOT paperless; use the trimmed/validated values.
            const parsedAddress = addressParse.success ? addressParse.data : undefined;
            const needsAddress =
                !v.paperless &&
                !!parsedAddress &&
                (storedAddress?.street1 !== parsedAddress.street1 ||
                    (storedAddress?.street2 ?? "") !== (parsedAddress.street2 ?? "") ||
                    storedAddress?.city !== parsedAddress.city ||
                    storedAddress?.state !== parsedAddress.state ||
                    storedAddress?.zipCode !== parsedAddress.zipCode);

            // Stored quote already reflects everything — nothing to persist, safe to advance.
            if (!needsPhone && !needsTerms && !needsPaperless && !needsAddress) return true;

            // Phone + terms persist through the regular quote update; paperless/mailing have their own
            // canonical endpoint. Only the quote update needs `updateQuote` — surface its absence rather
            // than silently advancing without the fields finalize relies on.
            if ((needsPhone || needsTerms) && !updateQuote) {
                console.error("[ca-checkout] updateQuote unavailable; cannot persist checkout info");
                setSaveError(true);
                return false;
            }
            setSaveError(false);
            setIsSaving(true);
            try {
                // 1) Phone + terms via the regular quote update (PUT).
                if (needsPhone || needsTerms) {
                    await updateQuote!.mutateAsync({
                        quote: {
                            ...quote,
                            ...(needsPhone ? { phone: v.phone } : {}),
                            ...(needsTerms ? { extra: { ...quote.extra, termsConsent: true } } : {})
                        }
                    });
                }

                // 2) Paperless (+ mailing when opted out) via the dedicated endpoint. Run after the quote
                // update so the endpoint's fresh read reflects phone/terms too — its response is the most
                // complete, so we use it to refresh the cache (incl. the scratchpad-backed paperless/mailing).
                if ((needsPaperless || needsAddress) && quote?.id) {
                    let body: PaperlessRequestBody;
                    if (v.paperless) {
                        body = { paperless: true };
                    } else {
                        if (!parsedAddress) {
                            // Defensive: handleSubmit's schema validation makes this unreachable, but if a
                            // future call path bypasses it, fail the save instead of throwing mid-checkout.
                            console.error("[ca-checkout] non-paperless persist without a valid mailing address");
                            setSaveError(true);
                            return false;
                        }
                        body = {
                            paperless: false,
                            mailing: {
                                street1: parsedAddress.street1,
                                street2: parsedAddress.street2,
                                city: parsedAddress.city,
                                state: parsedAddress.state,
                                zip: parsedAddress.zipCode
                            }
                        };
                    }
                    const updated = await new QuoteAPI(PublicConfig.PTZ_CA.UNDERWRITER).updatePaperless(quote.id, body);
                    queryClient.setQueryData(
                        ["quote", quote.id],
                        QuoteDataUtils.petQuoteToQuote(updated, PublicConfig.PTZ_CA.UNDERWRITER, {
                            includeBirthProps: true,
                            formatRatingZipcode: PtzCaDataUtils.formatRatingZipcode,
                            showNetPrice: PublicConfig.PTZ_CA.SHOW_NET_PRICE
                        })
                    );
                }

                return true;
            } catch (e) {
                // Province ⁄ postal mismatch is a field-level problem — surface it on the province select
                // rather than the generic save error.
                if (ErrorUtils.hasSpotError(e, "invalid-mailing-state")) {
                    setError("address.state", { message: Strings.PTZ_CA.MAILING_ADDRESS.STATE_MISMATCH });
                } else {
                    console.error("[ca-checkout] failed to save checkout info", e);
                    setSaveError(true);
                }
                return false;
            } finally {
                setIsSaving(false);
            }
        },
        [quote, updateQuote, queryClient, setError]
    );

    // The Continue handler. Always runnable (never blocked on validity): validates on click — surfacing
    // errors if invalid — and on valid persists the step, then clears dirty so Payment unlocks.
    // Resolves true when the step is valid and persisted (caller advances to Payment). Returns the result
    // explicitly rather than leaning on an `isTermsComplete` transition, so clicking Continue on an
    // already-complete step (e.g. after navigating back to Terms) still advances — there's no state
    // change to react to in that case.
    const submitStep = useCallback(async (): Promise<boolean> => {
        if (isSaving) return false;
        submitAttemptedRef.current = true;
        let succeeded = false;
        await handleSubmit(
            async data => {
                const ok = await persist(data);
                if (ok) {
                    setSavedOnce(true);
                    // Re-baseline the form to the persisted values so `isDirty` is false (step complete);
                    // a later edit flips it true again and re-locks Payment.
                    reset(data);
                    succeeded = true;
                }
            },
            // On invalid, scroll/focus the first errored field (matches the US scroll-to-error UX).
            formErrors => scrollToFirstError(formErrors)
        )();
        return succeeded;
    }, [handleSubmit, isSaving, persist, reset]);

    return {
        phone: values.phone,
        showPhoneError,
        handlePhoneChange,
        termsAccepted: values.termsConsent,
        termsError,
        handleTermsChange,
        isTermsComplete,
        saveError,
        isSaving,
        submitStep,
        // Paperless + mailing address
        paperless: values.paperless,
        handlePaperlessChange,
        address: {
            street1: values.address.street1,
            street2: values.address.street2,
            city: values.address.city,
            province: values.address.state,
            postal: values.address.zipCode
        } satisfies MailingAddressView,
        addressErrors,
        handleAddressChange,
        handleProvinceChange
    };
}
